import { supabase } from '../lib/supabase'
import { getCurrentUserId, verifyAgencyWriteAccess, verifyPropertyWriteAccess } from '../lib/auth'
import { portfolioSchema, portfolioUpdateSchema } from '../lib/validators'
import {
  logPortfolioCreated,
  logPortfolioUpdated,
  logPortfolioArchived,
  logPropertyAssignedToPortfolio,
  logPropertyRemovedFromPortfolio,
} from '../lib/audit'
import type { Portfolio, PortfolioInsert, PortfolioUpdate, PortfolioProperty } from '../types/common'

// ============================================================
// PORTFOLIO CRUD
// ============================================================

export async function getPortfolios(includeArchived = false, agentIdOverride?: string): Promise<Portfolio[]> {
  const userId = agentIdOverride ?? await getCurrentUserId()

  let query = supabase
    .from('portfolios')
    .select('*, portfolio_properties(property_id, properties!inner(id))')
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .is('portfolio_properties.properties.deleted_at', null)
    .order('name', { ascending: true })

  // TODO: archived-only filter — needs backend RPC pass
  if (!includeArchived) {
    query = query.eq('status', 'active')
  }

  const { data, error } = await query
  if (error) throw error

  return (data || []).map(p => ({
    ...p,
    property_count: p.portfolio_properties?.length ?? 0,
    portfolio_properties: undefined,
  })) as Portfolio[]
}

export async function getPortfolio(id: string, agentIdOverride?: string): Promise<Portfolio> {
  const ownerId = agentIdOverride ?? await getCurrentUserId()
  const { data, error } = await supabase
    .from('portfolios')
    .select('*, portfolio_properties(property_id, properties!inner(id))')
    .eq('id', id)
    .eq('owner_id', ownerId)
    .is('deleted_at', null)
    .is('portfolio_properties.properties.deleted_at', null)
    .single()

  if (error) throw error

  return {
    ...data,
    property_count: data.portfolio_properties?.length ?? 0,
    portfolio_properties: undefined,
  } as Portfolio
}

export async function createPortfolio(input: PortfolioInsert, agentId?: string | null): Promise<Portfolio> {
  const parsed = portfolioSchema.parse(input)
  const userId = await getCurrentUserId()
  const effectiveAgentId = agentId ?? userId

  const { data, error } = await supabase
    .from('portfolios')
    .insert({ ...parsed, owner_id: effectiveAgentId })
    .select()
    .single()

  if (error) throw error

  await logPortfolioCreated(data.id, { name: data.name, client_name: data.client_name })

  return { ...data, property_count: 0 } as Portfolio
}

export async function updatePortfolio(id: string, input: PortfolioUpdate): Promise<Portfolio> {
  const parsed = portfolioUpdateSchema.parse(input)
  await verifyAgencyWriteAccess()

  // Fetch old data for audit (RLS scopes by agency)
  const { data: oldData } = await supabase
    .from('portfolios')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!oldData) throw new Error('Portfolio not found')

  const { data, error } = await supabase
    .from('portfolios')
    .update(parsed)
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) throw error

  if (oldData) {
    await logPortfolioUpdated(
      id,
      {
        name: oldData.name,
        status: oldData.status,
        management_fee_type: oldData.management_fee_type,
        management_fee_value: oldData.management_fee_value,
        bank_account_name: oldData.bank_account_name,
        bank_sort_code: oldData.bank_sort_code,
        bank_account_number: oldData.bank_account_number,
      },
      {
        name: data.name,
        status: data.status,
        management_fee_type: data.management_fee_type,
        management_fee_value: data.management_fee_value,
        bank_account_name: data.bank_account_name,
        bank_sort_code: data.bank_sort_code,
        bank_account_number: data.bank_account_number,
      }
    )
  }

  return data as Portfolio
}

export async function archivePortfolio(id: string): Promise<Portfolio> {
  await verifyAgencyWriteAccess()
  const { data, error } = await supabase
    .from('portfolios')
    .update({ status: 'archived' })
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) throw error

  await logPortfolioArchived(id, { name: data.name })

  return data as Portfolio
}

export async function restorePortfolio(id: string): Promise<Portfolio> {
  await verifyAgencyWriteAccess()
  const { data, error } = await supabase
    .from('portfolios')
    .update({ status: 'active' })
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) throw error

  await logPortfolioUpdated(id, { status: 'archived' }, { status: 'active' })

  return data as Portfolio
}

export async function deletePortfolio(id: string): Promise<void> {
  await verifyAgencyWriteAccess()

  const { data: portfolio } = await supabase
    .from('portfolios')
    .select('name')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!portfolio) throw new Error('Portfolio not found')

  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'portfolios',
    record_id: id,
  })

  if (error) throw error

  await logPortfolioArchived(id, { name: portfolio.name, deleted: true })
}

// ============================================================
// PROPERTY-PORTFOLIO ASSIGNMENT
// ============================================================

export async function getPortfolioProperties(portfolioId: string): Promise<PortfolioProperty[]> {
  // Verify portfolio is visible to the current user (RLS scopes by owner for
  // agents, by agency + assignment for team members)
  const { data: portfolio } = await supabase.from('portfolios').select('id').eq('id', portfolioId).is('deleted_at', null).maybeSingle()
  if (!portfolio) throw new Error('Portfolio not found')

  const { data, error } = await supabase
    .from('portfolio_properties')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .order('assigned_at', { ascending: false })

  if (error) throw error
  return data as PortfolioProperty[]
}

export async function getPortfolioPropertyIds(portfolioId: string, agentIdOverride?: string): Promise<string[]> {
  // Verify portfolio belongs to current user (or the overridden agent for team members).
  // When agentIdOverride is passed (team member), RLS on portfolio_properties already
  // enforces access, so we use the agent's ID for the ownership check.
  const userId = agentIdOverride ?? await getCurrentUserId()
  const { data: portfolio } = await supabase.from('portfolios').select('id').eq('id', portfolioId).eq('owner_id', userId).is('deleted_at', null).maybeSingle()
  if (!portfolio) throw new Error('Portfolio not found')

  const { data, error } = await supabase
    .from('portfolio_properties')
    .select('property_id, properties!inner(id)')
    .eq('portfolio_id', portfolioId)
    .is('properties.deleted_at', null)

  if (error) throw error
  return (data || []).map(d => d.property_id)
}

export async function assignPropertyToPortfolio(
  propertyId: string,
  portfolioId: string
): Promise<PortfolioProperty> {
  const userId = await getCurrentUserId()

  // Verify the current user can write to this property (owner, agent, or team admin/manager)
  await verifyPropertyWriteAccess(propertyId)
  // Verify portfolio exists and is visible to the current user (RLS scopes by
  // owner for agents, by agency + assignment for team members)
  const { data: portfolio } = await supabase
    .from('portfolios')
    .select('id')
    .eq('id', portfolioId)
    .is('deleted_at', null)
    .maybeSingle()
  if (!portfolio) throw new Error('Portfolio not found')

  // Remove from any existing portfolio first (a property can only be in one)
  await supabase
    .from('portfolio_properties')
    .delete()
    .eq('property_id', propertyId)

  const { data, error } = await supabase
    .from('portfolio_properties')
    .insert({
      portfolio_id: portfolioId,
      property_id: propertyId,
      assigned_by: userId,
    })
    .select()
    .single()

  if (error) throw error

  await logPropertyAssignedToPortfolio(portfolioId, propertyId)

  return data as PortfolioProperty
}

export async function removePropertyFromPortfolio(propertyId: string): Promise<void> {
  // Verify the current user can write to this property (owner, agent, or team admin/manager)
  await verifyPropertyWriteAccess(propertyId)

  // Get current assignment for audit
  const { data: current } = await supabase
    .from('portfolio_properties')
    .select('portfolio_id')
    .eq('property_id', propertyId)
    .maybeSingle()

  const { error } = await supabase
    .from('portfolio_properties')
    .delete()
    .eq('property_id', propertyId)

  if (error) throw error

  if (current) {
    await logPropertyRemovedFromPortfolio(current.portfolio_id, propertyId)
  }
}

export async function getPropertyPortfolio(propertyId: string): Promise<Portfolio | null> {
  const { data, error } = await supabase
    .from('portfolio_properties')
    .select('portfolio_id, portfolios(id, owner_id, name, status, client_name, client_email, client_phone, notes, spend_approval_threshold_pence, management_fee_type, management_fee_value, created_at, updated_at)')
    .eq('property_id', propertyId)
    .maybeSingle()

  if (error) throw error
  if (!data) return null

  return (data as unknown as { portfolio_id: string; portfolios: Portfolio }).portfolios
}

// ============================================================
// BULK OPERATIONS
// ============================================================

export async function assignPropertiesToPortfolio(
  propertyIds: string[],
  portfolioId: string
): Promise<void> {
  const userId = await getCurrentUserId()

  // Verify portfolio exists and is visible to the current user (RLS scopes by
  // owner for agents, by agency + assignment for team members)
  const { data: portfolio } = await supabase
    .from('portfolios')
    .select('id')
    .eq('id', portfolioId)
    .is('deleted_at', null)
    .maybeSingle()
  if (!portfolio) throw new Error('Portfolio not found')

  // Verify the current user can write to all properties (owner, agent, or team admin/manager)
  for (const propertyId of propertyIds) {
    await verifyPropertyWriteAccess(propertyId)
  }

  // Remove existing assignments for these properties
  await supabase
    .from('portfolio_properties')
    .delete()
    .in('property_id', propertyIds)

  const rows = propertyIds.map(propertyId => ({
    portfolio_id: portfolioId,
    property_id: propertyId,
    assigned_by: userId,
  }))

  const { error } = await supabase
    .from('portfolio_properties')
    .insert(rows)

  if (error) throw error

  // Audit each assignment
  for (const propertyId of propertyIds) {
    await logPropertyAssignedToPortfolio(portfolioId, propertyId)
  }
}

export async function getUnassignedProperties(): Promise<string[]> {
  const userId = await getCurrentUserId()

  // Get all property IDs for this agent
  const { data: allProps, error: propsError } = await supabase
    .from('properties')
    .select('id')
    .eq('owner_id', userId)
    .is('deleted_at', null)

  if (propsError) throw propsError

  if (!allProps || allProps.length === 0) return []

  const allPropertyIds = allProps.map(p => p.id)

  // Get assigned property IDs
  const { data: assigned, error: assignedError } = await supabase
    .from('portfolio_properties')
    .select('property_id')
    .in('property_id', allPropertyIds)

  if (assignedError) throw assignedError

  const assignedIds = new Set((assigned || []).map(a => a.property_id))

  return allPropertyIds.filter(id => !assignedIds.has(id))
}

/**
 * Returns a map of property_id → portfolio name for all agent portfolios.
 * Used for labelling calendar events and other UI elements.
 */
export async function getPropertyPortfolioMap(): Promise<Record<string, string>> {
  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('portfolio_properties')
    .select('property_id, portfolios(name)')
    .eq('portfolios.owner_id', userId)

  if (error) throw error

  const map: Record<string, string> = {}
  for (const row of data || []) {
    const portfolioName = (row.portfolios as { name: string } | null)?.name
    if (portfolioName) {
      map[row.property_id] = portfolioName
    }
  }
  return map
}
